import { create } from 'zustand';

type TChatAdminAccount = {
  name?: string;
  avatar?: string;
};

type TChatAdminAccounts = {
  [uuid: string]: TChatAdminAccount;
};

type TUnreadCount = any; // TODO: Define proper type when BE is ready

type TDraftMessage = Record<string, { message: string; timestamp: number }>;
type TDraftFile = Record<string, File[]>;

type State = {
  unreadCount: TUnreadCount;
  adminAccounts: TChatAdminAccounts;
  draftMessages: TDraftMessage;
  draftFiles: TDraftFile;
  draftStorageKey: string | null;
};

type Actions = {
  setUnreadCount: (count: TUnreadCount) => void;
  setAdminAccounts: (accounts: TChatAdminAccounts) => void;
  initDrafts: (storageKey: string) => void;
  setDraftMessages: (state: TDraftMessage, storageKey?: string | null) => void;
  removeDraftMessage: (state: string) => void;
  setDraftFile: (state: TDraftFile) => void;
  removeDraftFile: (state: string) => void;
  resetStore: () => void;
};

const initialState: State = {
  unreadCount: {},
  adminAccounts: {},
  draftMessages: {},
  draftFiles: {},
  draftStorageKey: null,
};

function readDraftMessages(storageKey: string): TDraftMessage {
  try {
    const raw = localStorage.getItem(storageKey);
    return raw ? (JSON.parse(raw) as TDraftMessage) : {};
  } catch {
    return {};
  }
}

function persistDraftMessages(
  storageKey: string | null,
  draftMessages: TDraftMessage
): void {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(draftMessages));
  } catch {
    // ストレージ unavailable でも in-memory のドラフトは維持する
  }
}

const useMessageStore = create<State & Actions>((set) => ({
  ...initialState,
  setUnreadCount: (count: TUnreadCount) => {
    set(() => {
      return { unreadCount: count };
    });
  },
  setAdminAccounts: (accounts: TChatAdminAccounts) => {
    set(() => {
      return { adminAccounts: accounts };
    });
  },
  initDrafts: (storageKey: string) => {
    set(() => ({
      draftStorageKey: storageKey,
      draftMessages: readDraftMessages(storageKey),
    }));
  },
  setDraftMessages: (value: TDraftMessage, storageKey?: string | null) =>
    set((state) => {
      // ログアウトで初期化された後の保存は、表示中の下書きに混ぜず保存先だけ更新する
      if (storageKey && storageKey !== state.draftStorageKey) {
        persistDraftMessages(storageKey, {
          ...readDraftMessages(storageKey),
          ...value,
        });
        return state;
      }
      const draftMessages = { ...state.draftMessages, ...value };
      persistDraftMessages(state.draftStorageKey, draftMessages);
      return { draftMessages };
    }),
  removeDraftMessage: (channel: string) => {
    set((state) => {
      const newDraftMessage = { ...state.draftMessages };
      delete newDraftMessage[channel];
      persistDraftMessages(state.draftStorageKey, newDraftMessage);
      return { draftMessages: newDraftMessage };
    });
  },
  setDraftFile: (value: TDraftFile) =>
    set((state) => ({ draftFiles: { ...state.draftFiles, ...value } })),
  removeDraftFile: (channel: string) => {
    set((state) => {
      const newDraftFile = { ...state.draftFiles };
      delete newDraftFile[channel];
      return { draftFiles: newDraftFile };
    });
  },
  resetStore: () => {
    set(() => ({ ...initialState }));
  },
}));

export default useMessageStore;
